import React from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useLanguage } from "@/contexts/LanguageContext";
import CategoryBadge from "@/components/CategoryBadge";

import { PlannedInvestment, PlannedInvestmentTask } from "@/types";

interface PlannedMeasuresListProps {
  plannedMeasurements: (PlannedInvestment & {
    measure?: string;
    batchTitle?: string;
    tasks?: PlannedInvestmentTask[];
  })[];
}

const PlannedMeasuresList: React.FC<PlannedMeasuresListProps> = ({
  plannedMeasurements,
}) => {
  const { t, language } = useLanguage();

  if (plannedMeasurements.length === 0) {
    return (
      <p className="text-sm text-center text-muted-foreground py-4">
        {t("plannedMeasures.noMeasures")}
      </p>
    );
  }

  return (
    <Accordion type="single" collapsible className="space-y-2">
      {plannedMeasurements.map((item, index) => (
        <AccordionItem
          key={`planned-${index}`}
          value={`planned-${index}`}
          className="border rounded-lg overflow-hidden bg-white"
        >
          <AccordionTrigger className="px-4 py-2 hover:no-underline">
            <div className="flex flex-col items-start text-left">
              <span className="font-medium">
                {item.measure || t("plannedMeasures.unnamedMeasure")}
              </span>
              <div className="mt-1">
                <CategoryBadge category={item.category} size="xs" />
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-2 pb-4 text-sm">
            {item.description && (
              <div className="mt-2 mb-4">
                <p className="text-xs text-muted-foreground mb-1">
                  {t("plannedMeasures.description")}
                </p>
                <p>{item.description}</p>
              </div>
            )}

            {/* Tasks section */}
            {item.tasks && item.tasks.length > 0 && (
              <div className="mt-2">
                <div className="flex items-start gap-2">
                  <div className="text-xs text-muted-foreground mb-1">
                    {t("plannedMeasures.tasks")}
                  </div>
                </div>
                <div className="mb-4">
                  {item.tasks.map((task) => (
                    <div key={task.id} className="flex justify-between text-sm">
                      <span>{task.task}</span>
                      {task.cost > 0 && (
                        <div className="flex items-center gap-1">
                          <span className="font-medium">
                            {new Intl.NumberFormat(
                              language === "de" ? "de-DE" : "en-US",
                              {
                                style: "currency",
                                currency: "EUR",
                              }
                            ).format(Number(task.cost))}
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-muted-foreground mb-1">
                  {t("plannedMeasures.plannedAmount")}
                </p>
                <p className="font-medium">
                  {new Intl.NumberFormat(
                    language === "de" ? "de-DE" : "en-US",
                    {
                      style: "currency",
                      currency: "EUR",
                    }
                  ).format(Number(item.plannedBudget || 0))}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">
                  {t("plannedMeasures.batch")}
                </p>
                <p>{item.batchTitle}</p>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
};

export default PlannedMeasuresList;
